import { ReactElement, useState } from "react";
import { CardMedia, CardMediaProps, Skeleton, styled } from "@mui/material";

import { useImageWithToken } from "hooks/useImageWithToken";

interface Props extends CardMediaProps<"img"> {
  defaultIcon: ReactElement;
}

export function CardMediaLoader(props: Props) {
  const { defaultIcon, src, image, ...mediaProps } = props;

  // define image source
  const source = src || image || "";

  const [loadState, setLoadState] = useState<"init" | "error" | "completed">(
    source ? "init" : "error"
  );

  const { data: imageData } = useImageWithToken(source);

  return (
    <Styles sx={{ flex: `0 0 ${mediaProps.height}px` }} className={loadState}>
      {loadState === "init" && (
        <Skeleton
          variant="rectangular"
          height={mediaProps.height}
          width="100%"
        />
      )}
      {loadState !== "completed" && defaultIcon}
      {imageData && (
        <CardMedia
          component="img"
          className={loadState}
          color="primary"
          onLoad={() => setLoadState("completed")}
          onError={() => setLoadState("error")}
          src={imageData}
          {...mediaProps}
        />
      )}
    </Styles>
  );
}

const Styles = styled("div")`
  background-color: ${({ theme }) => theme.palette.background.default};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;

  &.init {
    position: relative;
    .MuiSkeleton-root {
      position: absolute;
      top: 0;
      left: 0;
    }
  }

  &.error {
    .MuiSkeleton-root {
      display: none;
    }
  }

  img {
    will-change: opacity;
    object-fit: contain;

    &.init {
      position: absolute;
      opacity: 0;
      transition: opacity 320ms ease-in;
    }
    &.error {
      display: none;
    }
    &.completed {
      opacity: 1;
      transition: opacity 320ms ease-in;
    }
  }
`;
